import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useUnifiedAuth } from "@/backend/use-unified-auth";
import { API_BASE_URL } from "@/backend/config";

const REFERRAL_STORAGE_KEY = "nordex_referral";
const REFERRAL_EXPIRY_DAYS = 30;
const REFERRAL_LINKED_KEY = "nordex_referral_linked";

interface StoredReferral {
  code: string;
  source: "ref" | "vanity";
  timestamp: number;
}

/**
 * Hook to capture and store referral codes from URL
 * Supports both ?ref=CODE and /join/SLUG formats
 * Also automatically links referral codes on first login for user referral system
 */
export function useReferralTracking() {
  const location = useLocation();
  const { userId, isAuthenticated } = useUnifiedAuth();

  // Capture referral codes from URL
  useEffect(() => {
    // Check for ?ref= query parameter
    const searchParams = new URLSearchParams(location.search);
    const refCode = searchParams.get("ref");

    if (refCode) {
      storeReferral(refCode, "ref");
      // Clean URL by removing ref parameter (optional, for cleaner URLs)
      // const newUrl = new URL(window.location.href);
      // newUrl.searchParams.delete("ref");
      // window.history.replaceState({}, "", newUrl.toString());
      return;
    }

    // Check for /join/SLUG path
    const joinMatch = location.pathname.match(/^\/join\/([a-zA-Z0-9-_]+)$/);
    if (joinMatch) {
      const vanitySlug = joinMatch[1];
      storeReferral(vanitySlug, "vanity");
      return;
    }
  }, [location]);

  // Link referral code on first login (for user referral system)
  useEffect(() => {
    if (!isAuthenticated || !userId) return;

    // Check if already linked for this user
    const linkedKey = `${REFERRAL_LINKED_KEY}_${userId}`;
    if (localStorage.getItem(linkedKey)) {
      return; // Already linked
    }

    // Get stored referral
    const storedReferral = getStoredReferral();
    if (!storedReferral) return;

    // Link the referral via API
    fetch(`${API_BASE_URL}/api/user-referrals/link?user_id=${userId}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ref_code: storedReferral.code }),
    })
      .then((response) => {
        if (response.ok) {
          console.log("[Referral] Successfully linked referral code");
          // Mark as linked for this user
          localStorage.setItem(linkedKey, "true");
          // Clear stored referral
          clearStoredReferral();
        } else {
          console.warn("[Referral] Failed to link referral code (may already be linked)");
        }
      })
      .catch((error) => {
        console.error("[Referral] Error linking referral code:", error);
      });
  }, [isAuthenticated, userId]);
}

/**
 * Store referral code in localStorage
 */
function storeReferral(code: string, source: "ref" | "vanity") {
  try {
    // Don't overwrite existing referral if it's not expired
    const existing = getStoredReferral();
    if (existing && !isExpired(existing.timestamp)) {
      return;
    }

    const referral: StoredReferral = {
      code,
      source,
      timestamp: Date.now(),
    };

    localStorage.setItem(REFERRAL_STORAGE_KEY, JSON.stringify(referral));
    console.log(`[Referral] Stored referral code: ${code} (source: ${source})`);
  } catch (error) {
    console.error("[Referral] Failed to store referral:", error);
  }
}

/**
 * Get stored referral code if not expired
 */
export function getStoredReferral(): StoredReferral | null {
  try {
    const stored = localStorage.getItem(REFERRAL_STORAGE_KEY);
    if (!stored) return null;

    const referral: StoredReferral = JSON.parse(stored);
    
    // Check if expired
    if (isExpired(referral.timestamp)) {
      localStorage.removeItem(REFERRAL_STORAGE_KEY);
      return null;
    }

    return referral;
  } catch (error) {
    console.error("[Referral] Failed to get stored referral:", error);
    return null;
  }
}

/**
 * Clear stored referral (e.g., after successful registration)
 */
export function clearStoredReferral() {
  try {
    localStorage.removeItem(REFERRAL_STORAGE_KEY);
  } catch (error) {
    console.error("[Referral] Failed to clear referral:", error);
  }
}

/**
 * Check if timestamp is expired
 */
function isExpired(timestamp: number): boolean {
  const expiryMs = REFERRAL_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
  return Date.now() - timestamp > expiryMs;
}

